/**
 * ============================================================================
 * RationaleForm — Phase 6c frontend (CCIE).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: Step 3 of the CC proposal wizard. Captures the human-readable
 *          title and the rationale. The rationale is the regulator-facing
 *          justification, so we enforce a 50-character minimum client-side
 *          before letting the user advance (the server also enforces).
 *
 * A11Y: live character-count region uses aria-live="polite" so SR users
 *  hear "47 of 50 characters" updates without each keystroke flooding the
 *  output (browser de-duplication keeps it sane).
 * ============================================================================ */

import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ChangeRequestProposal.module.css';

const RATIONALE_MIN = 50;

export interface RationaleFormProps {
  title: string;
  rationale: string;
  onTitleChange: (value: string) => void;
  onRationaleChange: (value: string) => void;
}

export function RationaleForm({
  title,
  rationale,
  onTitleChange,
  onRationaleChange,
}: RationaleFormProps) {
  const { t, i18n } = useTranslation();
  const titleId = useId();
  const rationaleId = useId();
  const helpId = useId();

  const trimmedLen = rationale.trim().length;
  const ok = trimmedLen >= RATIONALE_MIN;
  const nf = new Intl.NumberFormat(i18n.language);

  return (
    <div className={styles.rationaleForm}>
      <div className={styles.field}>
        <label htmlFor={titleId} className={styles.fieldLabel}>
          {t('changeControl.proposal.rationale.titleLabel', {
            defaultValue: 'Change title',
          })}
          <span className={styles.required} aria-hidden="true">
            *
          </span>
        </label>
        <p className={styles.fieldHelp}>
          {t('changeControl.proposal.rationale.titleHelp', {
            defaultValue:
              'One sentence. Surfaced in dashboards and notifications.',
          })}
        </p>
        <input
          id={titleId}
          type="text"
          className={styles.input}
          value={title}
          maxLength={140}
          onChange={(e) => onTitleChange(e.target.value)}
          placeholder={t('changeControl.proposal.rationale.titlePlaceholder', {
            defaultValue: 'e.g., Update calibration interval for HVAC-07',
          })}
          required
        />
      </div>

      <div className={styles.field}>
        <label htmlFor={rationaleId} className={styles.fieldLabel}>
          {t('changeControl.proposal.rationale.rationaleLabel', {
            defaultValue: 'Rationale',
          })}
          <span className={styles.required} aria-hidden="true">
            *
          </span>
        </label>
        <p id={helpId} className={styles.fieldHelp}>
          {t('changeControl.proposal.rationale.rationaleHelp', {
            defaultValue:
              'Why is this change needed? Include enough detail for the QA reviewer (min {{min}} characters).',
            min: RATIONALE_MIN,
          })}
        </p>
        <textarea
          id={rationaleId}
          className={styles.textarea}
          value={rationale}
          onChange={(e) => onRationaleChange(e.target.value)}
          aria-describedby={helpId}
          aria-invalid={!ok && rationale.length > 0}
          rows={6}
          placeholder={t('changeControl.proposal.rationale.rationalePlaceholder', {
            defaultValue:
              'Describe the trigger event, the proposed corrective action, and the expected GxP impact.',
          })}
          required
        />
        <div
          className={`${styles.charCount} ${ok ? styles.charCountOk : styles.charCountBelow}`}
          role="status"
          aria-live="polite"
        >
          {t('changeControl.proposal.rationale.charCount', {
            defaultValue: '{{actual}} of {{min}} characters',
            actual: nf.format(trimmedLen),
            min: nf.format(RATIONALE_MIN),
          })}
        </div>
      </div>
    </div>
  );
}
